import { useState } from 'react'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ConnectableAccount } from 'api/clients/IntegrationAuthsClient'
import { Drawer } from 'components/Drawer'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { Heading } from 'blaze-ui/Heading/Heading'
import { DrawerProps } from 'components/Drawer/Root'
import { SelectConnectableAccountsContent } from 'components/SelectConnectableAccountsModal/SelectConnectableAccountsModal'
import { pluralize } from 'utils'
import { Button as BlazeButton } from 'blaze-ui/Button'
import { Button } from 'blaze-components/WriterTopBar/Button'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

interface SelectConnectableAccountsDrawerProps extends DrawerProps {
  connectableAccounts: ConnectableAccount[]
  handleSubmit: (
    accounts: ConnectableAccount[],
    isRefreshOrOnCreated?: boolean | ((ids: number[]) => Promise<void>),
  ) => Promise<number[]>
  onCancel: () => void
  provider: ExternalIntegrationEnum
  isSingleSelect?: boolean
  allowReconnect?: boolean
}

export const SelectConnectableAccountsDrawer = observer(
  ({
    connectableAccounts,
    handleSubmit,
    onCancel,
    provider,
    isSingleSelect,
    allowReconnect,
    ...props
  }: SelectConnectableAccountsDrawerProps) => {
    const isVirtualMarketer = useCurrentWorkspace()?.is_virtual_marketer
    const [selectedAccounts, setSelectedAccounts] = useState<Array<ConnectableAccount>>(
      isSingleSelect ? [connectableAccounts[0]] : [],
    )
    const { drawersStore } = useRootStore()

    const handleCancel = () => {
      onCancel()
      drawersStore.closeDrawer()
    }

    return (
      <Drawer.Root {...props} onClose={handleCancel}>
        <Drawer.Portal>
          <Drawer.Content>
            <Drawer.InnerContent>
              <Drawer.Top>
                <div>
                  <Button onPress={handleCancel}>Cancel</Button>
                </div>
                <Drawer.Title asChild>
                  <Heading level={5}>Select which accounts to connect</Heading>
                </Drawer.Title>
              </Drawer.Top>
              <Drawer.Container>
                <SelectConnectableAccountsContent
                  selectedAccounts={selectedAccounts}
                  setSelectedAccounts={setSelectedAccounts}
                  connectableAccounts={connectableAccounts}
                  provider={provider}
                  isSingleSelect={isSingleSelect || isVirtualMarketer}
                  allowReconnect={allowReconnect}
                />

                <BlazeButton
                  size="lg"
                  fullWidth
                  data-testid="connect-account"
                  isDisabled={!selectedAccounts.length}
                  onPress={() => {
                    handleSubmit(selectedAccounts)
                    drawersStore.closeDrawer()
                  }}
                >
                  {`Connect ${selectedAccounts.length || ''} ${pluralize('account', selectedAccounts.length)}`}
                </BlazeButton>
              </Drawer.Container>
            </Drawer.InnerContent>
          </Drawer.Content>
          <Drawer.Overlay />
        </Drawer.Portal>
      </Drawer.Root>
    )
  },
)
